import React from 'react';

type DateFormatterProps = {
  value: string | Date;
  template?: 'condensed' | 'full' | 'LL h:mm a' | 'MMM D';
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const INVALID_DATE = 'Invalid date';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_MONTH = 146097 / 4800;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Callers can pass null or undefined at runtime, because some of their values are untyped.
function parseDate(value: string | Date | null | undefined) {
  if (value === undefined) {
    return new Date();
  }
  if (value === null) {
    return new Date(NaN);
  }
  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    // The Date constructor reads a date-only string as UTC. We show it as local midnight.
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
}

function getMonthName(date: Date) {
  return MONTHS[date.getMonth()];
}

function getShortMonthName(date: Date) {
  return getMonthName(date).slice(0, 3);
}

function formatTime(date: Date, { showSeconds }: { showSeconds: boolean }) {
  const hours = date.getHours();
  const hour = hours % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const meridiem = hours < 12 ? 'am' : 'pm';

  if (showSeconds) {
    return `${hour}:${minutes}:${seconds} ${meridiem}`;
  }
  return `${hour}:${minutes} ${meridiem}`;
}

function getStartOfWeek(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
}

function isSameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function isSameWeek(a: Date, b: Date) {
  return isSameDay(getStartOfWeek(a), getStartOfWeek(b));
}

// A short target month moves the day back. For example, January 31 plus one month is the last day of February.
function addCalendarMonths(date: Date, months: number) {
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(date.getDate(), lastDayOfMonth));
  return result;
}

function getCalendarDifference(a: Date, b: Date) {
  const [earlier, later] = [a, b].sort((x, y) => x.getTime() - y.getTime());

  let months = later.getMonth() - earlier.getMonth() + (later.getFullYear() - earlier.getFullYear()) * 12;
  if (addCalendarMonths(earlier, months) > later) {
    months -= 1;
  }

  const remainderDays = (later.getTime() - addCalendarMonths(earlier, months).getTime()) / MS_PER_DAY;

  return {
    totalDays: Math.round(months * DAYS_PER_MONTH) + remainderDays,
    totalMonths: months + remainderDays / DAYS_PER_MONTH,
  };
}

// The thresholds match the moment `fromNow` defaults.
function formatDuration(date: Date, now: Date) {
  if (Number.isNaN(date.getTime())) {
    return INVALID_DATE;
  }

  const { totalDays, totalMonths } = getCalendarDifference(date, now);
  const seconds = Math.round(totalDays * 24 * 60 * 60);
  const minutes = Math.round(totalDays * 24 * 60);
  const hours = Math.round(totalDays * 24);
  const days = Math.round(totalDays);
  const months = Math.round(totalMonths);
  const years = Math.round(totalMonths / 12);

  if (seconds < 45) {
    return 'a few seconds';
  }
  if (minutes <= 1) {
    return 'a minute';
  }
  if (minutes < 45) {
    return `${minutes} minutes`;
  }
  if (hours <= 1) {
    return 'an hour';
  }
  if (hours < 22) {
    return `${hours} hours`;
  }
  if (days <= 1) {
    return 'a day';
  }
  if (days < 26) {
    return `${days} days`;
  }
  if (months <= 1) {
    return 'a month';
  }
  if (months < 11) {
    return `${months} months`;
  }
  if (years <= 1) {
    return 'a year';
  }
  return `${years} years`;
}

function formatRelative(date: Date, now: Date) {
  const duration = formatDuration(date, now);

  if (date < now) {
    return `${duration} ago`;
  }
  return `${duration} from now`;
}

function formatCondensed(date: Date, now: Date) {
  if (isSameDay(date, now)) {
    return formatTime(date, { showSeconds: false });
  }
  if (isSameWeek(date, now)) {
    return WEEKDAYS[date.getDay()];
  }
  if (date.getFullYear() === now.getFullYear()) {
    return `${getShortMonthName(date)} ${date.getDate()}`;
  }
  return `${getShortMonthName(date)} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatWithTemplate(date: Date, template: NonNullable<DateFormatterProps['template']>, now: Date) {
  if (Number.isNaN(date.getTime())) {
    return INVALID_DATE;
  }

  const year = date.getFullYear();
  const day = date.getDate();

  switch (template) {
    case 'condensed':
      return formatCondensed(date, now);
    case 'full':
      return `${getShortMonthName(date)} ${day}, ${year} ${formatTime(date, { showSeconds: true })}`;
    case 'LL h:mm a':
      return `${getMonthName(date)} ${day}, ${year} ${formatTime(date, { showSeconds: false })}`;
    case 'MMM D':
      return `${getShortMonthName(date)} ${day}`;
  }
}

export default function DateFormatter({ value, template }: DateFormatterProps): JSX.Element {
  const date = parseDate(value);
  const now = new Date();

  if (template === undefined) {
    return <>{formatRelative(date, now)}</>;
  }

  return <>{formatWithTemplate(date, template, now)}</>;
}
